// src/components/Orders.js - Complete version with Professional Top Pagination
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'create'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  
  // Search state
  const [searchField, setSearchField] = useState('all'); // 'all', 'orderId', 'customerId', 'status', 'amount', 'created'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // For status dropdown
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize, setPageSize] = useState(20); // Allow changing page size
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Create order form state - simplified based on your API
  const [newOrder, setNewOrder] = useState({
    productId: 1,
    quantity: 1,
    price: 29.99
  });
  const [creating, setCreating] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!isSearchActive) {
      loadData();
    }
  }, [currentPage, isSearchActive, pageSize]); // Reload when page changes or when switching back to pagination

  const loadData = async (resetToFirstPage = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // If resetting to first page, update the current page
      const pageToLoad = resetToFirstPage ? 1 : currentPage;
      if (resetToFirstPage) {
        setCurrentPage(1);
      }
      
      // Load orders with pagination using your updated API service
      const ordersResponse = await apiService.getOrdersPaginated(pageToLoad, pageSize);
      console.log('Paginated orders data received:', ordersResponse);
      
      // Handle Spring Boot Page response structure
      const ordersData = ordersResponse.content || [];
      const totalCount = ordersResponse.totalElements || 0;
      const totalPagesCount = ordersResponse.totalPages || 1;
      
      setOrders(ordersData);
      setTotalOrders(totalCount);
      setTotalPages(totalPagesCount);
      
      // Try to load products (only once, not on every page load)
      if (currentPage === 1 || resetToFirstPage) {
        try {
          const productsData = await apiService.getAllProducts();
          setProducts(Array.isArray(productsData) ? productsData : []);
        } catch (productErr) {
          console.warn('Could not load products:', productErr);
          setProducts([]);
        }
      }
      
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load order data: ' + err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Search function - searches all orders
  const handleSearch = async () => {
    // For status search, use the selected status value instead of searchTerm
    const searchValue = searchField === 'status' ? selectedStatus : searchTerm;
    
    if (!searchValue.trim()) {
      if (isSearchActive) {
        clearSearch();
      }
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      let searchResults;
      if (apiService.searchOrders) {
        // If you have a dedicated search API endpoint
        searchResults = await apiService.searchOrders(searchValue, searchField);
      } else {
        // Fallback: fetch all orders and filter client-side
        const allOrdersResponse = await apiService.getAllOrders();
        const allOrders = Array.isArray(allOrdersResponse) ? allOrdersResponse : 
                         (allOrdersResponse.content || []);
        
        // Filter orders based on search value and field
        searchResults = allOrders.filter(order => {
          const searchLower = searchValue.toLowerCase();
          
          switch (searchField) {
            case 'orderId':
              return order.id.toString().includes(searchValue);
            case 'customerId':
              return order.customerId?.toLowerCase().includes(searchLower);
            case 'status':
              return order.status?.toLowerCase() === searchLower;
            case 'amount':
              return getOrderTotal(order).toString().includes(searchValue);
            case 'created':
              return formatDateShort(order.createdAt).includes(searchValue);
            default: // 'all'
              return (
                order.id.toString().includes(searchValue) ||
                order.customerId?.toLowerCase().includes(searchLower) ||
                order.status?.toLowerCase().includes(searchLower) ||
                order.paymentTransactionId?.toLowerCase().includes(searchLower) ||
                getOrderTotal(order).toString().includes(searchValue) ||
                formatDateShort(order.createdAt).includes(searchValue) ||
                (order.orderItems && order.orderItems.some(item => 
                  item.productName?.toLowerCase().includes(searchLower) ||
                  item.sku?.toLowerCase().includes(searchLower)
                ))
              );
          }
        });
      }
      
      setSearchResults(searchResults);
      setIsSearchActive(true);
      setExpandedRows(new Set()); // Clear expanded rows
      
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed: ' + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setSearchResults([]);
    setIsSearchActive(false);
    setExpandedRows(new Set());
  };

  // Navigate to specific page (replace current orders)
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  // Change page size
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Refresh data (go back to first page)
  const refreshData = () => {
    setExpandedRows(new Set()); // Clear expanded rows
    clearSearch(); // Clear search
    loadData(true); // Reset to first page
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    try {
      setCreating(true);
      setError(null);
      
      // Create order with your API structure
      const orderData = {
        productId: newOrder.productId,
        quantity: newOrder.quantity,
        price: newOrder.price
      };

      console.log('Creating order:', orderData);
      const createdOrder = await apiService.createOrder(orderData);
      
      // Refresh the list to show the new order
      refreshData();
      setNewOrder({ productId: 1, quantity: 1, price: 29.99 });
      setActiveTab('list');
      
      alert('Order created successfully!');
    } catch (err) {
      console.error('Failed to create order:', err);
      setError('Failed to create order: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      // Use the detailed endpoint if available, otherwise fallback to regular
      const orderDetails = await apiService.getOrderDetails ? 
        await apiService.getOrderDetails(orderId) : 
        await apiService.getOrderById(orderId);
      setSelectedOrder(orderDetails);
    } catch (err) {
      setError('Failed to load order details: ' + err.message);
    }
  };

  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
    const [year, month, day, hour = 0, minute = 0, second = 0] = dateArray;
    return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
  };

  const formatDateShort = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
    const [year, month, day] = dateArray;
    return month + '/' + day + '/' + year;
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING_PAYMENT': return '#f29808ff';
      case 'PROCESSING': return '#14c3eaff';
      case 'CONFIRMED': return '#0f9f05ff';
      case 'PAYMENT_SUCCESS': return '#0bea3bff';
      case 'SHIPPED': return '#1c0980ff';
      case 'PAYMENT_FAILED': return '#c40d19ff';
      case 'CANCELLED': return '#fe0f47ff';
      default: return '#10B981'; // Default to success green
    }
  };

  const getDisplayStatus = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING_PAYMENT': return 'PENDING';
      case 'PAYMENT_FAILED': return 'FAILED';
      case 'PAYMENT_SUCCESS': return 'SUCCESS';
      case 'CONFIRMED': return 'CONFIRMED';
      case 'PROCESSING': return 'PROCESSING';
      case 'CANCELLED': return 'CANCELLED';
      case 'SHIPPED': return 'SHIPPED';
      default: return 'SUCCESS'; // Default to SUCCESS to match mockup
    }
  };

  const getOrderTotal = (order) => {
    // Use totalAmount if available, otherwise calculate from orderItems
    if (order.totalAmount && order.totalAmount > 0) {
      return order.totalAmount;
    }
    
    if (order.orderItems && order.orderItems.length > 0) {
      return order.orderItems.reduce((total, item) => {
        return total + (item.quantity * item.price);
      }, 0);
    }
    
    // Fallback to order price
    return order.price || 0;
  };

  // Get the current dataset to work with (either paginated or search results)
  const getCurrentDataset = () => {
    return isSearchActive ? searchResults : orders;
  };

  // Sorting functionality (works on current dataset)
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedOrders = () => {
    const dataset = getCurrentDataset();
    if (!sortConfig.key) return dataset;

    return [...dataset].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for different data types
      switch (sortConfig.key) {
        case 'totalAmount':
          aValue = getOrderTotal(a);
          bValue = getOrderTotal(b);
          break;
        case 'createdAt':
        case 'updatedAt':
          aValue = aValue ? new Date(aValue[0], aValue[1] - 1, aValue[2], aValue[3] || 0, aValue[4] || 0, aValue[5] || 0) : new Date(0);
          bValue = bValue ? new Date(bValue[0], bValue[1] - 1, bValue[2], bValue[3] || 0, bValue[4] || 0, bValue[5] || 0) : new Date(0);
          break;
        case 'itemCount':
          aValue = a.orderItems ? a.orderItems.length : 0;
          bValue = b.orderItems ? b.orderItems.length : 0;
          break;
        default:
          // Handle string comparisons
          aValue = aValue || '';
          bValue = bValue || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Get filtered and sorted orders
  const getFilteredOrders = () => {
    return getSortedOrders();
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon">♦</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="sort-icon">▲</span> : 
      <span className="sort-icon">▼</span>;
  };

  // Row expansion functionality
  const toggleRowExpansion = (orderId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(orderId)) {
      newExpandedRows.delete(orderId);
    } else {
      newExpandedRows.add(orderId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Export ALL orders to CSV
  const exportAllOrdersToCSV = async () => {
    try {
      setError(null);
      const confirmExport = window.confirm(
        'This will export ALL orders from the database. This may take a moment. Continue?'
      );
      
      if (!confirmExport) return;
      
      // Show loading state
      setError('Fetching all orders for export...');
      
      // Fetch all orders from API
      const allOrdersResponse = await apiService.getAllOrders();
      const allOrders = Array.isArray(allOrdersResponse) ? allOrdersResponse : 
                       (allOrdersResponse.content || []);
      
      if (allOrders.length === 0) {
        setError('No orders found to export.');
        return;
      }
      
      const headers = [
        'Order ID',
        'Customer ID', 
        'Status',
        'Total Amount',
        'Created Date',
        'Updated Date',
        'Items Count',
        'Payment Transaction ID',
        'Item Details'
      ];

      const csvData = allOrders.map(order => {
        const itemDetails = order.orderItems && order.orderItems.length > 0 
          ? order.orderItems.map(item => 
              `${item.productName} (SKU: ${item.sku}, Qty: ${item.quantity}, Price: $${item.price})`
            ).join('; ')
          : 'No items';

        return [
          order.id,
          order.customerId || '',
          order.status || '',
          getOrderTotal(order).toFixed(2),
          formatDate(order.createdAt),
          formatDate(order.updatedAt),
          order.orderItems ? order.orderItems.length : 0,
          order.paymentTransactionId || '',
          itemDetails
        ];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      
      link.download = `all_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      setError(null);
      alert(`CSV export completed! Exported ${allOrders.length} total orders.`);
      
    } catch (error) {
      console.error('All orders CSV export failed:', error);
      setError(`Failed to export all orders to CSV: ${error.message}`);
    }
  };

  // Export ALL orders to PDF
  const exportAllOrdersToPDF = async () => {
    try {
      setError(null);
      const confirmExport = window.confirm(
        'This will export ALL orders from the database to PDF. This may take a moment. Continue?'
      );
      
      if (!confirmExport) return;
      
      // Show loading state
      setError('Fetching all orders for PDF export...');
      
      // Fetch all orders from API
      const allOrdersResponse = await apiService.getAllOrders();
      const allOrders = Array.isArray(allOrdersResponse) ? allOrdersResponse : 
                       (allOrdersResponse.content || []);
      
      if (allOrders.length === 0) {
        setError('No orders found to export.');
        return;
      }
      
      // Create a simple HTML table for printing
      const printWindow = window.open('', '_blank');
      
      printWindow.document.write('<!DOCTYPE html>');
      printWindow.document.write('<html><head><title>All Orders Export</title>');
      printWindow.document.write('<style>');
      printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
      printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
      printWindow.document.write('th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }');
      printWindow.document.write('th { background-color: #f2f2f2; font-weight: bold; }');
      printWindow.document.write('h1 { text-align: center; color: #333; }');
      printWindow.document.write('.summary { margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-radius: 5px; }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body>');
      
      printWindow.document.write('<h1>Complete Orders Export Report</h1>');
      
      printWindow.document.write('<div class="summary">');
      printWindow.document.write(`<p><strong>Export Type:</strong> All Orders</p>`);
      printWindow.document.write(`<p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>`);
      printWindow.document.write(`<p><strong>Total Orders:</strong> ${allOrders.length}</p>`);
      printWindow.document.write('</div>');
      
      printWindow.document.write('<table>');
      printWindow.document.write('<thead><tr>');
      printWindow.document.write('<th>Order ID</th>');
      printWindow.document.write('<th>Customer ID</th>');
      printWindow.document.write('<th>Status</th>');
      printWindow.document.write('<th>Amount</th>');
      printWindow.document.write('<th>Created</th>');
      printWindow.document.write('<th>Items</th>');
      printWindow.document.write('</tr></thead>');
      
      printWindow.document.write('<tbody>');
      allOrders.forEach(order => {
        printWindow.document.write('<tr>');
        printWindow.document.write(`<td>${order.id}</td>`);
        printWindow.document.write(`<td>${(order.customerId || '').substring(0, 12)}</td>`);
        printWindow.document.write(`<td>${getDisplayStatus(order.status)}</td>`);
        printWindow.document.write(`<td>$${getOrderTotal(order).toFixed(2)}</td>`);
        printWindow.document.write(`<td>${formatDateShort(order.createdAt)}</td>`);
        printWindow.document.write(`<td>${order.orderItems ? order.orderItems.length : 0}</td>`);
        printWindow.document.write('</tr>');
      });
      printWindow.document.write('</tbody></table>');
      
      printWindow.document.write('<script>window.onload = function() { window.print(); }</script>');
      printWindow.document.write('</body></html>');
      
      printWindow.document.close();
      setError(null);
      
    } catch (error) {
      console.error('All orders PDF export failed:', error);
      setError(`Failed to export all orders to PDF: ${error.message}`);
    }
  };

  // Export current list to CSV
  const exportCurrentListToCSV = () => {
    try {
      const dataToExport = getFilteredOrders();
      const exportType = isSearchActive ? 'Search Results' : 'Current Page';
      
      const headers = [
        'Order ID',
        'Customer ID', 
        'Status',
        'Total Amount',
        'Created Date',
        'Updated Date',
        'Items Count',
        'Payment Transaction ID',
        'Item Details'
      ];

      const csvData = dataToExport.map(order => {
        const itemDetails = order.orderItems && order.orderItems.length > 0 
          ? order.orderItems.map(item => `${item.productName} (SKU: ${item.sku}, Qty: ${item.quantity}, Price: $${item.price})`).join('; ')
          : 'No items';

        return [
          order.id,
          order.customerId || '',
          order.status || '',
          getOrderTotal(order).toFixed(2),
          formatDate(order.createdAt),
          formatDate(order.updatedAt),
          order.orderItems ? order.orderItems.length : 0,
          order.paymentTransactionId || '',
          itemDetails
        ];
      });

      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      
      const filename = isSearchActive 
        ? `orders_search_results_${new Date().toISOString().split('T')[0]}.csv`
        : `orders_page_${currentPage}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      link.click();
      
      alert(`CSV export completed! Exported ${dataToExport.length} orders from ${exportType.toLowerCase()}.`);
    } catch (error) {
      console.error('Current list CSV export failed:', error);
      setError('Failed to export current list to CSV: ' + error.message);
    }
  };

  // Export current list to PDF
  const exportCurrentListToPDF = () => {
    try {
      const dataToExport = getFilteredOrders();
      const exportType = isSearchActive ? 'Search Results' : `Page ${currentPage}`;
      
      const printWindow = window.open('', '_blank');
      
      printWindow.document.write('<!DOCTYPE html>');
      printWindow.document.write('<html><head><title>Orders Export</title>');
      printWindow.document.write('<style>');
      printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
      printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
      printWindow.document.write('th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }');
      printWindow.document.write('th { background-color: #f2f2f2; }');
      printWindow.document.write('h1 { text-align: center; }');
      printWindow.document.write('.summary { margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-radius: 5px; }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body>');
      
      printWindow.document.write('<h1>Orders Export Report</h1>');
      printWindow.document.write('<div class="summary">');
      printWindow.document.write(`<p><strong>Export Type:</strong> ${exportType}</p>`);
      printWindow.document.write(`<p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>`);
      printWindow.document.write(`<p><strong>Total Orders:</strong> ${dataToExport.length}</p>`);
      if (isSearchActive) {
        const searchText = searchField === 'status' ? selectedStatus : searchTerm;
        printWindow.document.write(`<p><strong>Search:</strong> ${searchField} = "${searchText}"</p>`);
      }
      printWindow.document.write('</div>');
      
      printWindow.document.write('<table>');
      printWindow.document.write('<thead><tr>');
      printWindow.document.write('<th>Order ID</th>');
      printWindow.document.write('<th>Customer ID</th>');
      printWindow.document.write('<th>Status</th>');
      printWindow.document.write('<th>Amount</th>');
      printWindow.document.write('<th>Created</th>');
      printWindow.document.write('<th>Items</th>');
      printWindow.document.write('</tr></thead>');
      
      printWindow.document.write('<tbody>');
      dataToExport.forEach(order => {
        printWindow.document.write('<tr>');
        printWindow.document.write(`<td>${order.id}</td>`);
        printWindow.document.write(`<td>${(order.customerId || '').substring(0, 12)}</td>`);
        printWindow.document.write(`<td>${getDisplayStatus(order.status)}</td>`);
        printWindow.document.write(`<td>$${getOrderTotal(order).toFixed(2)}</td>`);
        printWindow.document.write(`<td>${formatDateShort(order.createdAt)}</td>`);
        printWindow.document.write(`<td>${order.orderItems ? order.orderItems.length : 0}</td>`);
        printWindow.document.write('</tr>');
      });
      printWindow.document.write('</tbody></table>');
      
      printWindow.document.write('<script>window.onload = function() { window.print(); }</script>');
      printWindow.document.write('</body></html>');
      
      printWindow.document.close();
      
    } catch (error) {
      console.error('Current list PDF export failed:', error);
      setError('Failed to export current list to PDF: ' + error.message);
    }
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading && currentPage === 1 && !isSearchActive) {
    return <div className="loading">Loading orders...</div>;
  }

  const filteredOrders = getFilteredOrders();
  const displayOrders = filteredOrders;

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1></h1>
        <div className="tab-buttons">
          <button 
            className={'tab-btn ' + (activeTab === 'list' ? 'active' : '')}
            onClick={() => setActiveTab('list')}
          >
            Order List
          </button>
          <button 
            className={'tab-btn ' + (activeTab === 'create' ? 'active' : '')}
            onClick={() => setActiveTab('create')}
          >
            Create Order
          </button>
          <button 
            onClick={refreshData} 
            className="tab-btn" 
            disabled={loading || searchLoading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {activeTab === 'list' && displayOrders.length > 0 && (
            <div className="export-buttons-group">
              {/* Current List Export Buttons */}
              <div className="export-section">
                <span className="export-label">Export :Current List:</span>
                <button 
                  onClick={exportCurrentListToCSV} 
                  className="tab-btn export-btn"
                  disabled={loading || searchLoading}
                  title={`Export current ${isSearchActive ? 'search results' : 'page'} to CSV`}
                >
                  📄 CSV
                </button>
                <button 
                  onClick={exportCurrentListToPDF} 
                  className="tab-btn export-btn"
                  disabled={loading || searchLoading}
                  title={`Export current ${isSearchActive ? 'search results' : 'page'} to PDF`}
                >
                  📋 PDF
                </button>
              </div>
              
              {/* All Orders Export Buttons */}
              <div className="export-section">
                <span className="export-label">All Orders:</span>
                <button 
                  onClick={exportAllOrdersToCSV} 
                  className="tab-btn export-btn export-all-btn"
                  disabled={loading || searchLoading}
                  title="Export ALL orders from database to CSV"
                >
                  📄 All CSV
                </button>
                <button 
                  onClick={exportAllOrdersToPDF} 
                  className="tab-btn export-btn export-all-btn"
                  disabled={loading || searchLoading}
                  title="Export ALL orders from database to PDF"
                >
                  📋 All PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="orders-list">
          {/* Professional Top Pagination - Only show when not in search mode */}
          {!isSearchActive && displayOrders.length > 0 && (
            <div className="top-pagination-container">
              {/* Left section - Stats */}
              <div className="pagination-info-left">
                <div className="pagination-stats">
                  <div className="pagination-stats-icon"></div>
                  <span className="pagination-count">{totalOrders.toLocaleString()}</span>
                  <span>total orders</span>
                </div>
                <span className="pagination-info">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>
              </div>

              {/* Center section - Navigation */}
              <div className="pagination-controls-center">
                {/* First page */}
                <button 
                  className="pagination-nav-btn"
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  ⏮
                </button>
                
                {/* Previous page */}
                <button 
                  className="pagination-nav-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  ◀
                </button>
                
                {/* Page numbers */}
                {generatePageNumbers().map(page => (
                  <button
                    key={page}
                    className={`pagination-nav-btn ${page === currentPage ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
                
                {/* Next page */}
                <button 
                  className="pagination-nav-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  ▶
                </button>
                
                {/* Last page */}
                <button 
                  className="pagination-nav-btn"
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                >
                  ⏭
                </button>

                {/* Direct page input */}
                <div className="pagination-input-group">
                  <span className="pagination-input-label">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    className="pagination-input"
                    placeholder={currentPage.toString()}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          goToPage(page);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Right section - Actions */}
              <div className="pagination-actions-right">
                {/* Page size selector */}
                <div className="page-size-selector">
                  <span>Show:</span>
                  <select 
                    className="page-size-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>per page</span>
                </div>

                {/* Refresh button */}
                <button 
                  className="pagination-refresh-btn"
                  onClick={refreshData}
                  disabled={loading}
                  title="Refresh data"
                >
                  <div className="refresh-icon"></div>
                  Refresh
                </button>
              </div>
            </div>
          )}

          <div className="orders-header-section">
            <div className="orders-title-section">
              <h3>
                {/* {isSearchActive ? 
                  `Search Results (${displayOrders.length} found)` : 
                  `Your Orders (${totalOrders.toLocaleString()} total)`
                } */}
              </h3>
            </div>
            
            {/* Search Section */}
            <div className="search-container">
              <div className="search-controls-row">
                {/* Search Field Dropdown */}
                <div className="search-field-group">
                  <label className="search-field-label">Search by:</label>
                  <select 
                    value={searchField} 
                    onChange={(e) => {
                      setSearchField(e.target.value);
                      // Clear search values when changing field
                      setSearchTerm('');
                      setSelectedStatus('');
                    }}
                    className="search-field-select"
                  >
                    <option value="all">All Fields</option>
                    <option value="orderId">Order ID</option>
                    <option value="customerId">Customer ID</option>
                    <option value="status">Status</option>
                    <option value="amount">Amount</option>
                    <option value="created">Created Date</option>
                  </select>
                </div>

                {/* Status Dropdown - Only show when status is selected */}
                {searchField === 'status' && (
                  <div className="search-field-group">
                    <label className="search-field-label">Status:</label>
                    <select 
                      value={selectedStatus} 
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="search-field-select"
                    >
                      <option value="">Select Status</option>
                      <option value="cancelled">CANCELLED</option>
                      <option value="payment_failed">PAYMENT_FAILED</option>
                      <option value="payment_success">PAYMENT_SUCCESS</option>
                      <option value="processing">PROCESSING</option>
                      <option value="shipped">SHIPPED</option>
                    </select>
                  </div>
                )}

                {/* Clear Button */}
                {(searchTerm || selectedStatus || isSearchActive) && (
                  <div className="clear-button-group">
                    <button 
                      onClick={clearSearch}
                      className="clear-search-btn"
                      title="Clear search"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Search Input and Button Row */}
              <div className="search-input-row">
                {/* Search Input - Only show when not status field */}
                {searchField !== 'status' && (
                  <div className="search-input-group">
                    <input
                      type="text"
                      placeholder={`Search ${searchField === 'all' ? 'orders' : searchField}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                )}

                {/* Search Button */}
                <div className="search-button-group">
                  <button 
                    onClick={handleSearch}
                    disabled={searchLoading || (searchField === 'status' ? !selectedStatus : !searchTerm)}
                    className="search-btn"
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Search Status */}
              {isSearchActive && (
                <div className="search-status-row">
                  <div className="search-results-info">
                    <span className="search-icon">🔍</span>
                    <span className="search-details">
                      Found <strong>{displayOrders.length}</strong> orders 
                      {searchField !== 'all' && ` in ${searchField}`}
                      {searchField === 'status' && selectedStatus && ` matching "${selectedStatus.toUpperCase()}"`}
                      {searchField !== 'status' && searchTerm && ` matching "${searchTerm}"`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {displayOrders.length === 0 && !loading && !searchLoading ? (
            isSearchActive ? (
              <div className="no-orders">
                <h3>No Search Results</h3>
                <p>
                  No orders found matching "
                  {searchField === 'status' ? selectedStatus.toUpperCase() : searchTerm}"
                  {searchField !== 'all' && ` in ${searchField}`}. Try a different search term.
                </p>
                <button 
                  onClick={clearSearch} 
                  className="create-first-order-btn"
                >
                  Clear Search
                </button>
              </div>
            ) : (
              <div className="no-orders">
                <h3>No Orders Found</h3>
                <p>No orders found. Create your first order!</p>
                <button 
                  onClick={() => setActiveTab('create')} 
                  className="create-first-order-btn"
                >
                  Create First Order
                </button>
              </div>
            )
          ) : (
            <>
              <div className="orders-table-modern">
                <table>
                  <thead>
                    <tr>
                      <th className="expand-column"></th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('id')}
                      >
                        Order ID {getSortIcon('id')}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('customerId')}
                      >
                        Customer ID {getSortIcon('customerId')}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('status')}
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('totalAmount')}
                      >
                        Amount {getSortIcon('totalAmount')}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('createdAt')}
                      >
                        Created {getSortIcon('createdAt')}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort('itemCount')}
                      >
                        Qty {getSortIcon('itemCount')}
                      </th>
                      <th className="actions-column">Act</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayOrders.map(order => (
                      <React.Fragment key={order.id}>
                        <tr className={expandedRows.has(order.id) ? 'expanded-row' : ''}>
                          <td>
                            <button 
                              className="tab-btn-active"
                              onClick={() => toggleRowExpansion(order.id)}
                            >
                              {expandedRows.has(order.id) ? '−' : '+'}
                            </button>
                          </td>
                          <td className="order-id-cell">{order.id}</td>
                          <td className="customer-id-cell">{(order.customerId || '').substring(0, 8) + '...'}</td>
                          <td>
                            <span 
                              className="status-badge-modern"
                              style={{ 
                                color: getStatusColor(order.status),  
                                backgroundColor: "white", 
                                borderColor: "black", 
                                border: "2px solid"
                              }}
                            >
                              {getDisplayStatus(order.status)}
                            </span>
                          </td>
                          <td className="amount-cell-modern">${getOrderTotal(order).toFixed(2)}</td>
                          <td className="date-cell">{formatDateShort(order.createdAt)}</td>
                          <td className="qty-cell-modern">
                            {order.orderItems && order.orderItems.length > 0 ? (
                              order.orderItems.length
                            ) : (
                              <span className="no-items">0</span>
                            )}
                          </td>
                          <td>
                            <button 
                              className="action-btn-modern" 
                              onClick={() => viewOrderDetails(order.id)}
                            >
                              V
                            </button>
                          </td>
                        </tr>
                        {expandedRows.has(order.id) && (
                          <tr className="expanded-content">
                            <td colSpan="8">
                              <div className="expanded-details">
                                <div className="expanded-section">
                                  <h4>Order Details</h4>
                                  <div className="detail-grid-inline">
                                    <div className="detail-item-inline">
                                      <strong>Full Customer ID:</strong> {order.customerId}
                                    </div>
                                    <div className="detail-item-inline">
                                      <strong>Created:</strong> {formatDate(order.createdAt)}
                                    </div>
                                    <div className="detail-item-inline">
                                      <strong>Updated:</strong> {formatDate(order.updatedAt)}
                                    </div>
                                    {order.paymentTransactionId && (
                                      <div className="detail-item-inline">
                                        <strong>Payment Transaction ID:</strong> {order.paymentTransactionId}
                                      </div>
                                    )}
                                    {order.refundAmount && (
                                      <div className="detail-item-inline">
                                        <strong>Refund Amount:</strong> ${order.refundAmount.toFixed(2)}
                                      </div>
                                    )}
                                    {order.refundReason && (
                                      <div className="detail-item-inline">
                                        <strong>Refund Reason:</strong> {order.refundReason}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {order.orderItems && order.orderItems.length > 0 && (
                                  <div className="expanded-section">
                                    <h4>Order Items</h4>
                                    <div className="items-grid">
                                      {order.orderItems.map(item => (
                                        <div key={item.id} className="item-card">
                                          <div className="item-name">{item.productName}</div>
                                          <div className="item-details">
                                            <span>SKU: {item.sku}</span>
                                            <span>Qty: {item.quantity}</span>
                                            <span>Price: ${(item.price || 0).toFixed(2)}</span>
                                            <span>Total: ${((item.quantity || 0) * (item.price || 0)).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Search results info */}
              {isSearchActive && displayOrders.length > 0 && (
                <div className="search-results-info">
                  <div className="search-results-summary">
                    <span className="results-count">
                      Found {displayOrders.length} orders
                      {searchField !== 'all' && ` in ${searchField}`}
                      {searchField === 'status' && selectedStatus && ` matching "${selectedStatus.toUpperCase()}"`}
                      {searchField !== 'status' && searchTerm && ` matching "${searchTerm}"`}
                    </span>
                    <button 
                      onClick={clearSearch}
                      className="return-to-pagination-btn"
                    >
                      Return to Pagination View
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="create-order">
          <h3>Create New Order</h3>
          
          <form onSubmit={handleCreateOrder} className="order-form">
            <div className="form-group">
              <label htmlFor="productId">Product ID:</label>
              <input
                type="number"
                id="productId"
                min="1"
                value={newOrder.productId}
                onChange={(e) => setNewOrder({...newOrder, productId: parseInt(e.target.value)})}
                required
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="quantity">Quantity:</label>
              <input
                type="number"
                id="quantity"
                min="1"
                value={newOrder.quantity}
                onChange={(e) => setNewOrder({...newOrder, quantity: parseInt(e.target.value)})}
                required
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="price">Unit Price:</label>
              <input
                type="number"
                id="price"
                step="0.01"
                min="0.01"
                value={newOrder.price}
                onChange={(e) => setNewOrder({...newOrder, price: parseFloat(e.target.value)})}
                required
                className="form-control"
              />
            </div>

            <div className="order-summary">
              <div className="summary-row">
                <span>Total Amount: </span>
                <strong>${(newOrder.quantity * newOrder.price).toFixed(2)}</strong>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setActiveTab('list')}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={creating}
                className="submit-btn"
              >
                {creating ? 'Creating...' : 'Create Order'}
              </button>
            </div>
          </form>
        </div>
      )}

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details #{selectedOrder.id}</h3>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="details-section">
                <h4>Order Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Order ID:</label>
                    <span>{selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Customer ID:</label>
                    <span>{selectedOrder.customerId}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span>{selectedOrder.status}</span>
                  </div>
                  <div className="detail-item">
                    <label>Total Amount:</label>
                    <span className="amount">${getOrderTotal(selectedOrder).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created:</label>
                    <span>{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  {selectedOrder.updatedAt && (
                    <div className="detail-item">
                      <label>Updated:</label>
                      <span>{formatDate(selectedOrder.updatedAt)}</span>
                    </div>
                  )}
                  {selectedOrder.paymentTransactionId && (
                    <div className="detail-item">
                      <label>Payment Transaction:</label>
                      <span>{selectedOrder.paymentTransactionId}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
                <div className="details-section">
                  <h4>Order Items</h4>
                  <div className="items-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.orderItems.map(item => (
                          <tr key={item.id}>
                            <td>{item.productName}</td>
                            <td>{item.sku}</td>
                            <td>{item.quantity}</td>
                            <td>${(item.price || 0).toFixed(2)}</td>
                            <td>${((item.quantity || 0) * (item.price || 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedOrder.refundAmount && (
                <div className="details-section">
                  <h4>Refund Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Refund Amount:</label>
                      <span>${(selectedOrder.refundAmount || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Refund Reason:</label>
                      <span>{selectedOrder.refundReason}</span>
                    </div>
                    <div className="detail-item">
                      <label>Refund Date:</label>
                      <span>{formatDate(selectedOrder.refundTimestamp)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;